import { format as dateFnsFormat } from 'date-fns';
import i18n, { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { isCurrentEnvironment } from '@/utils/environment';
import { syncDocumentLang } from '@/utils/syncDocumentLang';

import Constants from '@/constants';

import { DotNestedKeys } from '@/types/utils';

import en from './locales/en.json';
import es from './locales/es.json';

import { store } from '@/atoms/store';
import { userPrefsAtom } from '@/atoms/userPrefs';

const debug = isCurrentEnvironment(Constants.DEV);

export const defaultNS = 'translation';

export const resources = {
  es: { translation: es },
  en: { translation: en },
} as const;

export type Resources = typeof resources;

/** ## `i18next` translation keys. */
export type TranslationKey = DotNestedKeys<Resources['en']['translation']>;

let initialization: Promise<unknown> | undefined;

/**
 * Initializes i18next exactly once. Anything that touches i18next before the
 * app has rendered — `initializeApp`'s user bootstrap in particular — must
 * await this first: `changeLanguage` dereferences the resource store i18next
 * only creates during `init`, so calling it on an uninitialized instance
 * throws and leaves the app permanently unrendered.
 */
export const initializeTranslation = async () => {
  initialization ??= runInitialization();
  await initialization;
};

const runInitialization = async () => {
  const languageCode = window.native.isNative
    ? window.language
    : store.get(userPrefsAtom).language ||
      Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;

  const initOptions: InitOptions = {
    debug,
    lng: languageCode,
    fallbackLng: Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
    load: 'currentOnly',
    keySeparator: '.',
    defaultNS,
    interpolation: {
      escapeValue: false, // react already protects against XSS
      format: (value: Date, format?: string) => {
        const f = format ?? '';
        if (f === 'locale-string' && value.toLocaleString()) {
          return value.toLocaleString();
        } else if (f === 'date-full') {
          return dateFnsFormat(value, 'MM/dd/yyyy');
        } else if (f === 'date-long') {
          return dateFnsFormat(value, 'MMMM do, yyyy');
        } else if (f === 'date-voice') {
          return dateFnsFormat(value, 'MMMM do yyyy');
        }
        return value as unknown as string;
      },
    },
    // Note: We were using the http backend but ran into issues due to translation files being cached.
    // Dynamic webpack imports might be possible but Loren could not quickly solve so went with static for now.
    resources,
    saveMissing: true,
    missingInterpolationHandler: (text: string, value: unknown) => {
      console.warn('Missing Interpolation', text, value);
    },
  };

  // cast to any/initOptions to satisfy overload even though we know the shape is correct
  await i18n.use(initReactI18next).init(initOptions as unknown as InitOptions);

  syncDocumentLang(i18n.language);
  i18n.on('languageChanged', syncDocumentLang);
};
